import { PROGRAM_DAY_KEYS, PROGRAM_SLOTS_PER_DAY } from "./programState";

const METADATA_FIELDS = Object.freeze(["title", "clientName", "startDate", "endDate"]);
const EXERCISE_FIELDS = Object.freeze(["sets", "reps", "rest", "intensity", "tempo", "notes"]);

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const isValidLocation = (programState, location) => {
  const { dayKey, slotIndex } = location || {};
  const slots = programState?.days?.[dayKey]?.slots;

  return (
    Array.isArray(slots) &&
    Number.isInteger(slotIndex) &&
    slotIndex >= 0 &&
    slotIndex < slots.length
  );
};

const getExercise = (programState, location) => {
  if (!isValidLocation(programState, location)) return null;

  return programState.days[location.dayKey].slots[location.slotIndex];
};

const sameLocation = (first, second) =>
  first?.dayKey === second?.dayKey && first?.slotIndex === second?.slotIndex;

const replaceSlots = (programState, replacements) => {
  if (replacements.length === 0) return programState;

  const nextDays = { ...programState.days };
  const copiedSlots = {};

  replacements.forEach(({ location, value }) => {
    const { dayKey, slotIndex } = location;

    if (!copiedSlots[dayKey]) {
      copiedSlots[dayKey] = [...programState.days[dayKey].slots];
    }

    copiedSlots[dayKey][slotIndex] = value;
  });

  Object.entries(copiedSlots).forEach(([dayKey, slots]) => {
    nextDays[dayKey] = {
      ...programState.days[dayKey],
      slots,
    };
  });

  return {
    ...programState,
    days: nextDays,
  };
};

const pickExerciseChanges = (changes, ignoreEmptyStrings) => {
  if (!changes || typeof changes !== "object") return {};

  return EXERCISE_FIELDS.reduce((result, field) => {
    if (!hasOwn(changes, field)) return result;
    if (ignoreEmptyStrings && changes[field] === "") return result;

    return {
      ...result,
      [field]: changes[field],
    };
  }, {});
};

const normalizeLocations = (locations) => (Array.isArray(locations) ? locations : [locations]);

export const getOccupiedLocations = (programState) =>
  PROGRAM_DAY_KEYS.reduce((locations, dayKey) => {
    const dayLocations = programState.days[dayKey].slots.reduce((result, slot, slotIndex) => {
      if (!slot) return result;

      return [...result, { dayKey, slotIndex }];
    }, []);

    return [...locations, ...dayLocations];
  }, []);

export const updateProgramMetadata = (programState, changes) => {
  if (!changes || typeof changes !== "object") return programState;

  const metadataChanges = METADATA_FIELDS.reduce((result, field) => {
    if (!hasOwn(changes, field)) return result;

    return {
      ...result,
      [field]: changes[field],
    };
  }, {});

  if (Object.keys(metadataChanges).length === 0) return programState;

  return {
    ...programState,
    ...metadataChanges,
  };
};

export const setProgramNotes = (programState, notes) => ({
  ...programState,
  notes,
});

export const setDayLabel = (programState, dayKey, label) => {
  if (!programState?.days?.[dayKey]) return programState;

  return {
    ...programState,
    days: {
      ...programState.days,
      [dayKey]: {
        ...programState.days[dayKey],
        label,
      },
    },
  };
};

export const placeExercise = (programState, location, exercise) => {
  if (!isValidLocation(programState, location)) return programState;

  const exerciseId = exercise?.exerciseId ?? exercise?.id;
  if (exerciseId === null || exerciseId === undefined) return programState;

  return replaceSlots(programState, [
    {
      location,
      value: {
        exerciseId,
        name: exercise.name ?? exercise.exerciseName ?? "",
        sets: "",
        reps: "",
        rest: "",
        intensity: "",
        tempo: "",
        notes: "",
      },
    },
  ]);
};

export const removeExercise = (programState, location) => {
  if (!getExercise(programState, location)) return programState;

  return replaceSlots(programState, [{ location, value: null }]);
};

export const moveExercise = (programState, from, to) => {
  if (
    !isValidLocation(programState, from) ||
    !isValidLocation(programState, to) ||
    sameLocation(from, to)
  ) {
    return programState;
  }

  const sourceExercise = getExercise(programState, from);
  const targetExercise = getExercise(programState, to);

  if (!sourceExercise || targetExercise) return programState;

  return replaceSlots(programState, [
    { location: from, value: null },
    { location: to, value: sourceExercise },
  ]);
};

export const swapExercises = (programState, first, second) => {
  if (
    !isValidLocation(programState, first) ||
    !isValidLocation(programState, second) ||
    sameLocation(first, second)
  ) {
    return programState;
  }

  const firstExercise = getExercise(programState, first);
  const secondExercise = getExercise(programState, second);

  if (!firstExercise || !secondExercise) return programState;

  return replaceSlots(programState, [
    { location: first, value: secondExercise },
    { location: second, value: firstExercise },
  ]);
};

export const updateExercise = (programState, location, changes) => {
  const exercise = getExercise(programState, location);
  const exerciseChanges = pickExerciseChanges(changes, false);

  if (!exercise || Object.keys(exerciseChanges).length === 0) return programState;

  return replaceSlots(programState, [
    {
      location,
      value: {
        ...exercise,
        ...exerciseChanges,
      },
    },
  ]);
};

export const updateExercises = (programState, locations, changes) => {
  const exerciseChanges = pickExerciseChanges(changes, true);
  if (Object.keys(exerciseChanges).length === 0) return programState;

  const replacements = normalizeLocations(locations).reduce((result, location) => {
    const exercise = getExercise(programState, location);
    if (!exercise) return result;

    return [
      ...result,
      {
        location,
        value: {
          ...exercise,
          ...exerciseChanges,
        },
      },
    ];
  }, []);

  return replaceSlots(programState, replacements);
};

export const clearExerciseFields = (programState, locations, fields) => {
  const requestedFields = Array.isArray(fields) ? fields : [fields];
  const fieldsToClear = requestedFields.filter((field) => EXERCISE_FIELDS.includes(field));

  if (fieldsToClear.length === 0) return programState;

  const replacements = normalizeLocations(locations).reduce((result, location) => {
    const exercise = getExercise(programState, location);
    if (!exercise) return result;

    const clearedFields = fieldsToClear.reduce(
      (cleared, field) => ({ ...cleared, [field]: "" }),
      {}
    );

    return [
      ...result,
      {
        location,
        value: {
          ...exercise,
          ...clearedFields,
        },
      },
    ];
  }, []);

  return replaceSlots(programState, replacements);
};

export const clearAllExerciseFields = (programState, locations) =>
  clearExerciseFields(programState, locations, EXERCISE_FIELDS);

export const clearDay = (programState, dayKey) => {
  if (!programState?.days?.[dayKey]) return programState;

  return {
    ...programState,
    days: {
      ...programState.days,
      [dayKey]: {
        label: "",
        slots: Array.from({ length: PROGRAM_SLOTS_PER_DAY }, () => null),
      },
    },
  };
};

export const clearProgramContent = (programState) => ({
  ...programState,
  notes: "",
  days: PROGRAM_DAY_KEYS.reduce(
    (days, dayKey) => ({
      ...days,
      [dayKey]: {
        label: "",
        slots: Array.from({ length: PROGRAM_SLOTS_PER_DAY }, () => null),
      },
    }),
    {}
  ),
});
